<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useField } from 'vee-validate'
import { UploadCloud, Eye, Loader2, Trash2, AlertCircle } from 'lucide-vue-next'
import type { FormField } from '../../types/formTypeStructure'
import FormFieldWrapper from '../../FormFieldWrapper.vue'
import { useFileBucketManager } from '../../hooks/useFileBucketManager'
import { extractFileNameFromUrl } from '../../utils/fileName'
import { getLocalizedText } from '../../utils/formHelpers'

interface Props {
  field: FormField & { fieldType: 'IMAGE' }
  name?: string
  currentLang?: string
  defaultValue?: string
  className?: string
}

interface UploadedImageInfo {
  url: string
  name: string
  size?: number
  type?: string
  bucketFilename?: string
}

const props = defineProps<Props>()
const fieldName = props.name ?? props.field.fieldName.toString()
const label = computed(() => getLocalizedText(props.field.label, props.currentLang ?? 'fr'))
const isRequired = computed(() => props.field.validations?.some(v => v.validationType === 'required'))

const {
  value,
  errorMessage: fieldErrorMessage,
  handleChange,
  setValue,
  setTouched
} = useField<string | null>(fieldName, undefined, {
  initialValue: props.defaultValue ?? null
})

const dragActive = ref(false)
const uploadedImage = ref<UploadedImageInfo | null>(null)
const localPreviewUrl = ref<string | null>(null)
const errorMessage = ref<string | null>(null)
const fileInputRef = ref<HTMLInputElement | null>(null)
const didInit = ref(false)

const fileBucketManager = computed(() => 
  props.field.fileToBucketManage
    ? useFileBucketManager(props.field.fileToBucketManage)
    : null
)

const hasUploadSupport = computed(() => fileBucketManager.value?.hasUploadSupport ?? false)
const hasDeleteSupport = computed(() => fileBucketManager.value?.hasDeleteSupport ?? false)
const isUploading = computed(() => fileBucketManager.value?.isUploading ?? false)
const isDeleting = computed(() => fileBucketManager.value?.isDeleting ?? false)
const bucketError = computed(() => fileBucketManager.value?.error ?? null)

const effectiveError = computed(() => errorMessage.value ?? bucketError.value ?? fieldErrorMessage.value)

// Initialiser avec defaultValue
watch(() => props.defaultValue, (newValue) => {
  if (typeof newValue === 'string' && newValue.trim().length > 0 && !didInit.value) {
    uploadedImage.value = {
      url: newValue,
      name: extractFileNameFromUrl(newValue) ?? newValue,
      bucketFilename: extractFileNameFromUrl(newValue) ?? undefined,
    }
    setValue(newValue, false)
    didInit.value = true
  }
}, { immediate: true })

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const handleDrag = (e: DragEvent) => {
  e.preventDefault()
  e.stopPropagation()
  dragActive.value = e.type === 'dragenter' || e.type === 'dragover'
}

const handleDrop = (e: DragEvent) => {
  e.preventDefault()
  e.stopPropagation()
  dragActive.value = false
  if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
    handleFile(e.dataTransfer.files[0])
  }
}

const handleFile = async (file: File) => {
  // Vérifier que c'est bien une image
  if (!file.type.startsWith('image/')) {
    alert('Veuillez sélectionner un fichier image valide')
    return
  }

  if (!hasUploadSupport.value) {
    errorMessage.value = 'Configuration d\'upload manquante pour ce champ (fileToBucketManage).'
    return
  }

  fileBucketManager.value?.resetError()
  errorMessage.value = null

  const localUrl = URL.createObjectURL(file)
  localPreviewUrl.value = localUrl

  try {
    const result = await fileBucketManager.value!.uploadFile({ file })
    uploadedImage.value = {
      url: result.url,
      name: result.originalName ?? result.filename ?? extractFileNameFromUrl(result.url) ?? file.name,
      size: file.size,
      type: file.type,
      bucketFilename: result.filename ?? extractFileNameFromUrl(result.url),
    }
    setValue(result.url, true)
    setTouched(true)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Échec de l\'upload de l\'image.'
    errorMessage.value = message
  } finally {
    URL.revokeObjectURL(localUrl)
    localPreviewUrl.value = null
  }
}

const handleInputChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  if (target.files && target.files.length > 0) {
    handleFile(target.files[0])
  }
}

const removeImage = async () => {
  fileBucketManager.value?.resetError()
  errorMessage.value = null

  if (uploadedImage.value && hasDeleteSupport.value) {
    try {
      await fileBucketManager.value!.deleteFile({
        filename: uploadedImage.value.bucketFilename ?? extractFileNameFromUrl(uploadedImage.value.url ?? '') ?? undefined,
        fileUrl: uploadedImage.value.url,
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Échec de la suppression du fichier distant.'
      errorMessage.value = message
      return
    }
  }

  uploadedImage.value = null
  setValue(null, true)
  setTouched(true)
  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
}

const openImage = () => {
  if (uploadedImage.value?.url) {
    window.open(uploadedImage.value.url, '_blank')
  }
}
</script>

<template>
  <div class="image-field-container">
    <!-- Label dupliqué au-dessus -->
    <div class="image-field-label-container">
      <span class="image-field-label-text">
        {{ label }}
        <AlertCircle
          v-if="isRequired"
          :size="16"
          class="inline text-red-500 ml-2"
        />
      </span>
    </div>

    <FormFieldWrapper :field="props.field" :current-lang="props.currentLang">
      <div class="space-y-4">
        <!-- Zone de drop -->
        <div
          v-if="!uploadedImage && !localPreviewUrl"
          :class="[
            'relative border-2 border-dashed rounded-lg cursor-pointer transition-all duration-300 ease-in-out',
            dragActive
              ? 'border-[#04406E] bg-[#e6f0f5] scale-[1.02]'
              : 'border-gray-300 hover:border-[#04406E] hover:bg-gray-50',
            props.className,
            isUploading && 'pointer-events-none opacity-60'
          ]"
          @dragenter="handleDrag"
          @dragleave="handleDrag"
          @dragover="handleDrag"
          @drop="handleDrop"
          @click="fileInputRef?.click()"
        >
          <input
            ref="fileInputRef"
            type="file"
            accept="image/*"
            class="hidden"
            @change="handleInputChange"
          />

          <div class="p-8 text-center">
            <div class="flex flex-col items-center space-y-4">
              <div
                :class="[
                  'p-3 rounded-full transition',
                  dragActive
                    ? 'bg-[#04406E] text-white'
                    : 'bg-gray-100 text-[#04406E] hover:bg-[#e6f0f5]'
                ]"
              >
                <UploadCloud :size="24" />
              </div>

              <div>
                <p class="text-base font-medium text-gray-700 mb-1">
                  {{ dragActive ? 'Déposez votre image ici' : 'Glissez-déposez votre image' }}
                </p>
                <p class="text-sm text-gray-500">
                  ou
                  <span class="text-[#04406E] font-medium hover:underline">
                    cliquez pour parcourir
                  </span>
                </p>
                <p class="text-xs text-gray-400 mt-2">
                  Formats acceptés : JPG, PNG, GIF, WEBP
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Preview de l'image -->
        <div
          v-if="uploadedImage || localPreviewUrl"
          class="relative border border-gray-200 rounded-lg overflow-hidden bg-gray-50"
        >
          <div class="relative group">
            <img
              :src="localPreviewUrl ?? uploadedImage?.url ?? ''"
              alt="Aperçu"
              class="w-full h-auto max-h-96 object-contain"
            />

            <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
              <div class="flex space-x-2">
                <button
                  v-if="uploadedImage"
                  type="button"
                  @click="openImage"
                  class="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                >
                  <Eye :size="20" class="text-[#04406E]" />
                </button>
                <button
                  type="button"
                  @click="removeImage"
                  :disabled="isDeleting"
                  class="p-2 bg-white rounded-full hover:bg-red-100 transition-colors disabled:opacity-60"
                >
                  <Loader2 v-if="isDeleting" :size="20" class="animate-spin text-red-600" />
                  <Trash2 v-else :size="20" class="text-red-600" />
                </button>
              </div>
            </div>
          </div>

          <div class="p-4 bg-white border-t border-gray-200">
            <div class="flex items-center justify-between">
              <div class="flex-1 min-w-0">
                <p class="font-medium text-gray-800 truncate">
                  {{ uploadedImage?.name }}
                </p>
                <p v-if="uploadedImage?.size || localPreviewUrl" class="text-sm text-gray-500">
                  {{ uploadedImage?.size ? formatFileSize(uploadedImage.size) : '' }}
                </p>
              </div>
              <Loader2 v-if="isUploading" :size="20" class="animate-spin text-[#04406E]" />
            </div>
          </div>
        </div>

        <div
          v-if="effectiveError"
          class="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-600"
        >
          {{ effectiveError }}
        </div>
      </div>
    </FormFieldWrapper>
  </div>
</template>

<style scoped>
.image-field-container {
  position: relative;
  display: block;
  width: 100%;
}

.image-field-label-container {
  position: relative;
  display: block;
  margin-bottom: 0.75rem;
  z-index: 100;
}

.image-field-label-text {
  display: block;
  font-size: 1.125rem;
  font-weight: 700;
  color: #333333;
  line-height: 1.2;
  margin: 0;
  padding: 0;
  position: static !important;
  transform: none !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
  bottom: auto !important;
  height: auto !important;
  overflow: visible !important;
  text-align: left !important;
  text-overflow: unset !important;
  white-space: normal !important;
  pointer-events: auto !important;
  border: none !important;
  transform-origin: unset !important;
  transition: none !important;
}
</style>

